// @ts-nocheck
import { createClient } from '@supabase/supabase-js'
import type { SupabaseClient, User } from '@supabase/supabase-js'
import { createElement, useEffect, useState } from 'react'

interface SSOPayload {
  access_token: string
  refresh_token?: string
  supabase_url: string
  publishable_key: string
}

interface SSOState {
  supabase: SupabaseClient | null
  user: User | null
  loading: boolean
  error: string | null
}

const fallbackSupabaseUrl = import.meta.env.VITE_SUPABASE_URL
const fallbackSupabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY
const PORTAL_URL = 'https://portal.fvlconsorcios.com.br'

const SSO_CREDENTIALS_KEY = 'fvl_sso_credentials'

interface StoredSSOCredentials {
  supabase_url: string
  publishable_key: string
}

function saveSSOCredentials(creds: StoredSSOCredentials) {
  try {
    localStorage.setItem(SSO_CREDENTIALS_KEY, JSON.stringify(creds))
  } catch {
    // localStorage indisponivel — ignora
  }
}

function loadSSOCredentials(): StoredSSOCredentials | null {
  try {
    const raw = localStorage.getItem(SSO_CREDENTIALS_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as StoredSSOCredentials
    if (!parsed.supabase_url || !parsed.publishable_key) return null
    return parsed
  } catch {
    return null
  }
}

let supabaseClient: SupabaseClient | null = null
let initAttempted = false

function createBrowserClient(url: string, key: string) {
  return createClient(url, key, {
    auth: {
      autoRefreshToken: true,
      persistSession: true,
    },
  })
}

function ensureFallbackClient(): SupabaseClient | null {
  if (supabaseClient) {
    return supabaseClient
  }

  // Prioriza credenciais SSO salvas — permite retomar a sessao apos fechar o navegador
  // sem precisar passar de novo pelo portal.
  const stored = loadSSOCredentials()
  if (stored) {
    supabaseClient = createBrowserClient(stored.supabase_url, stored.publishable_key)
    return supabaseClient
  }

  if (!fallbackSupabaseUrl || !fallbackSupabaseAnonKey) {
    return null
  }

  supabaseClient = createBrowserClient(fallbackSupabaseUrl, fallbackSupabaseAnonKey)
  return supabaseClient
}

export function hasSupabaseConfiguration() {
  return Boolean(
    supabaseClient || loadSSOCredentials() || (fallbackSupabaseUrl && fallbackSupabaseAnonKey),
  )
}

export async function initSSO(): Promise<SupabaseClient | null> {
  initAttempted = true

  if (supabaseClient) {
    return supabaseClient
  }

  const params = new URLSearchParams(window.location.search)
  const ssoToken = params.get('sso_token')

  if (!ssoToken) {
    return ensureFallbackClient()
  }

  const cleanUrl = new URL(window.location.href)
  cleanUrl.searchParams.delete('sso_token')
  window.history.replaceState({}, document.title, cleanUrl.toString())

  let payload: SSOPayload

  try {
    payload = JSON.parse(atob(ssoToken)) as SSOPayload
  } catch {
    throw new Error('Token SSO invalido ou corrompido. Acesse novamente pelo Portal FVL.')
  }

  const { access_token, refresh_token, supabase_url, publishable_key } = payload

  if (!access_token || !supabase_url || !publishable_key) {
    throw new Error('Token SSO incompleto. Acesse novamente pelo Portal FVL.')
  }

  const ssoClient = createBrowserClient(supabase_url, publishable_key)
  const { error } = await ssoClient.auth.setSession({
    access_token,
    refresh_token: refresh_token ?? '',
  })

  if (error) {
    throw new Error(`Sessao SSO invalida: ${error.message}. Acesse novamente pelo Portal FVL.`)
  }

  saveSSOCredentials({ supabase_url, publishable_key })
  supabaseClient = ssoClient
  return ssoClient
}

export function getSupabaseClient(): SupabaseClient {
  if (supabaseClient) {
    return supabaseClient
  }

  const fallbackClient = ensureFallbackClient()
  if (fallbackClient) {
    return fallbackClient
  }

  if (!initAttempted) {
    throw new Error('initSSO() ainda nao foi chamado. Inicialize o app antes de usar o Supabase.')
  }

  throw new Error('Cliente Supabase indisponivel. Configure o ambiente ou acesse pelo Portal FVL.')
}

export function useSSO(): SSOState {
  const [state, setState] = useState<SSOState>({
    supabase: null,
    user: null,
    loading: true,
    error: null,
  })

  useEffect(() => {
    let cancelled = false

    async function loadSession() {
      try {
        const client = await initSSO()
        const activeClient = client ?? ensureFallbackClient()

        if (!activeClient) {
          if (!cancelled) {
            setState({
              supabase: null,
              user: null,
              loading: false,
              error: 'Nenhuma configuracao de autenticacao foi encontrada.',
            })
          }
          return
        }

        const {
          data: { user },
          error,
        } = await activeClient.auth.getUser()

        if (cancelled) {
          return
        }

        if (error || !user) {
          setState({
            supabase: activeClient,
            user: null,
            loading: false,
            error: error?.message ?? 'Sessao invalida ou expirada.',
          })
          return
        }

        setState({
          supabase: activeClient,
          user,
          loading: false,
          error: null,
        })
      } catch (error) {
        if (cancelled) {
          return
        }

        setState({
          supabase: null,
          user: null,
          loading: false,
          error: error instanceof Error ? error.message : 'Erro ao inicializar a sessao SSO.',
        })
      }
    }

    void loadSession()

    return () => {
      cancelled = true
    }
  }, [])

  return state
}

export function InvalidSession({ message }: { message?: string }) {
  return createElement(
    'div',
    {
      style: {
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#020617',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
        padding: '1rem',
      },
    },
    createElement(
      'div',
      {
        style: {
          textAlign: 'center',
          maxWidth: '400px',
          padding: '2.5rem',
          backgroundColor: '#0f172a',
          border: '1px solid #1e293b',
          borderRadius: '1rem',
        },
      },
      createElement(
        'h1',
        {
          style: {
            color: '#f1f5f9',
            fontSize: '1.25rem',
            fontWeight: 700,
            marginBottom: '0.75rem',
          },
        },
        'Sessao invalida',
      ),
      createElement(
        'p',
        {
          style: {
            color: '#94a3b8',
            fontSize: '0.9rem',
            marginBottom: '1.5rem',
            lineHeight: 1.6,
          },
        },
        message ?? 'Sessao invalida. Acesse pelo Portal FVL.',
      ),
      createElement(
        'a',
        {
          href: PORTAL_URL,
          style: {
            display: 'inline-block',
            backgroundColor: '#4f46e5',
            color: '#fff',
            padding: '0.75rem 2rem',
            borderRadius: '0.75rem',
            textDecoration: 'none',
            fontWeight: 600,
            fontSize: '0.9rem',
          },
        },
        'Ir para o Portal FVL',
      ),
    ),
  )
}
